import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PlusIcon } from '@heroicons/react/outline'
import { toast } from 'react-toastify'

import { LoadingCircle } from 'components/Loading'
import { Pagination } from 'components/Pagination'
import { SearchBar } from 'components/Search'
import { ProtectedSection } from 'components/ProtectedSection'
import { Destroy } from 'components/Buttons/Destroy'

import { Role } from 'models'
import { StaffPermission } from 'models/staffPermission'
import { ReadResult, QueryOptions } from 'interfaces/queryOptions'
import { listClientStaffPermissions } from 'modules/Clients/services/read'
import { destroyPermission } from 'modules/Staff/services/api'
import { translations } from 'modules/Staff/translations'
import config from 'config'

import { CreateClientStaffPermission } from './components/Create'

export function StaffTab() {
  const { clientId } = useParams() as unknown as { clientId: string }

  const [permissions, setPermissions] = useState<ReadResult<StaffPermission>[]>(
    []
  )
  const [assignedStaffIds, setAssignedStaffIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [lastPage, setLastPage] = useState(1)

  const maxItemsPerPage = config.items_per_list_page

  const loadPermissions = useCallback(
    async (options: QueryOptions = {}) => {
      if (!clientId) return
      setIsLoading(true)
      try {
        const { count, data } = await listClientStaffPermissions(clientId, {
          includes: { staff_member: ['*'] },
          count: true,
          limit: maxItemsPerPage,
          page,
          search,
          ...options
        })
        setPermissions(data as ReadResult<StaffPermission>[])
        setTotal(count)
        setAssignedStaffIds(
          (data
            .map((p: any) => p.relationships?.staff_member?.id?.toString())
            .filter(Boolean) as string[]) || []
        )
      } catch {
        toast.error('Falha ao buscar staff members')
      } finally {
        setIsLoading(false)
      }
    },
    [clientId, maxItemsPerPage, page, search]
  )

  useEffect(() => {
    loadPermissions()
  }, [loadPermissions])

  useEffect(() => {
    setLastPage(Math.ceil(total / maxItemsPerPage))
  }, [total, maxItemsPerPage])

  const onPageChange = useCallback(
    (p: number) => setPage(Math.min(Math.max(1, p), lastPage)),
    [lastPage]
  )

  const onSearch = useCallback((term: string) => {
    if (!term || term.length < config.min_search_length) term = ''
    setSearch(term)
    setPage(1)
  }, [])

  return (
    <>
      <div>
        <div className="flex items-center">
          <div className="py-6 flex flex-grow flex-row justify-between animate-fade-in-down">
            <SearchBar value={search} onChange={onSearch} />
          </div>
          <ProtectedSection roles={[Role.MANAGER]}>
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="h-9 flex items-center justify-center text-sm font-medium rounded-md text-emerald-600 border border-emerald-600 hover:bg-emerald-600 hover:text-white"
            >
              <div className="flex flex-row items-center justify-center px-2">
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
                <div className="mr-2 ml-1">Adicionar staff</div>
              </div>
            </button>
          </ProtectedSection>
        </div>

        {isLoading ? (
          <div className="py-8">
            <LoadingCircle />
          </div>
        ) : (
          <div className="flex flex-col animate-fade-in-down">
            <div className="py-2 inline-block min-w-full">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-light text-gray-500 uppercase">
                        Nome
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-light text-gray-500 uppercase">
                        Papel
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-light text-gray-500 uppercase">
                        Email
                      </th>
                      <ProtectedSection roles={[Role.MANAGER]}>
                        <th className="px-4 py-3 text-left text-sm font-light text-gray-500 uppercase">
                          Ações
                        </th>
                      </ProtectedSection>
                    </tr>
                  </thead>
                  <tbody className="text-left bg-white divide-y divide-gray-200">
                    {permissions.map((permission: any, idx: number) => {
                      const staffAttributes =
                        permission?.relationships?.staff_member?.attributes
                      const staffId =
                        permission?.relationships?.staff_member?.id
                      return (
                        <tr
                          key={idx}
                          className={idx % 2 ? 'bg-gray-50' : 'bg-white'}
                        >
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                            <div className="text-xs font-semibold text-gray-900">
                              {staffAttributes?.fullname || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                            <div className="text-xs text-gray-500">
                              {translations.role[staffAttributes?.user_type] ||
                                staffAttributes?.user_type ||
                                '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                            <div className="text-xs text-gray-500">
                              {staffAttributes?.email || '-'}
                            </div>
                          </td>
                          <ProtectedSection roles={[Role.MANAGER]}>
                            <td className="px-4 py-3 text-xs text-left text-gray-500">
                              <Destroy
                                id={staffId}
                                complementaryId={clientId}
                                destroy={destroyPermission}
                                successMessage="Permissão revogada com sucesso"
                                onDestroy={() => window.location.reload()}
                              />
                            </td>
                          </ProtectedSection>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination
              pageSize={maxItemsPerPage}
              currentPage={page}
              itemsOnPage={permissions.length}
              totalItems={total}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>

      {isCreateOpen && (
        <CreateClientStaffPermission
          open={isCreateOpen}
          setOpen={setIsCreateOpen}
          clientId={clientId}
          assignedStaffIds={assignedStaffIds}
        />
      )}
    </>
  )
}
